import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import ChatRoom from './ChatRoom';
import './GlobalChatWidget.scss';

interface ChatRoom {
  id: number;
  name: string;
  content_object: string;
  last_message_time_ago: string;
  unread_count: number;
  has_unread: boolean;
  is_public: boolean;
  last_message?: {
    content: string;
    message_type: string;
    sender_name: string;
  };
}

interface OpenChatWindow {
  id: number;
  chatRoom: ChatRoom;
  minimized: boolean;
}

const GlobalChatWidget: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [showConversationList, setShowConversationList] = useState(false);
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [openChats, setOpenChats] = useState<OpenChatWindow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalUnread, setTotalUnread] = useState(0);

  // Hide widget on /chat page and scanner pages
  const isChatPage = location.pathname.startsWith('/chat');
  const isScannerPage = location.pathname.startsWith('/scanner/');
  const shouldHide = isChatPage || isScannerPage;

  // Fetch chat rooms - on-demand only
  const fetchChatRooms = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.chat.getChatRooms();
      
      if (response.success) {
        setChatRooms(response.chat_rooms || []);
        const total = (response.chat_rooms || []).reduce((sum: number, room: ChatRoom) => sum + (room.unread_count || 0), 0);
        setTotalUnread(total);
      }
    } catch (err: any) {
      const status = err.response?.status;
      
      // Handle 403 Forbidden - user doesn't have permission
      // Silently fail for widget (don't show error, just don't load chats)
      if (status === 403) {
        setChatRooms([]);
        setTotalUnread(0);
        // Don't set error for widget - it's a permission issue, not a critical error
        if (process.env.NODE_ENV === 'development') {
          console.warn('Chat access forbidden (403) - user may not have permission');
        }
      } else if (status === 401) {
        // 401 Unauthorized - user not authenticated
        setChatRooms([]);
        setTotalUnread(0);
        // Don't show error in widget for auth issues
      } else {
        // Other errors - log but don't show in widget
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching chat rooms:', err);
        }
        setError(err.message || 'Failed to load conversations');
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch when window gains focus (only if widget is open)
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const handleFocus = () => {
      // Only fetch if user has the widget open
      if (isOpen || showConversationList) {
        fetchChatRooms();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isAuthenticated, isOpen, showConversationList, fetchChatRooms]);

  // Open a chat room
  const handleOpenChat = useCallback((chatRoom: ChatRoom) => {
    setOpenChats(prev => {
      // Check if chat is already open
      const existingChat = prev.find(chat => chat.id === chatRoom.id);
      if (existingChat) {
        // If minimized, restore it
        if (existingChat.minimized) {
          return prev.map(chat => 
            chat.id === chatRoom.id ? { ...chat, minimized: false } : chat
          );
        }
        // Bring to front
        const filtered = prev.filter(chat => chat.id !== chatRoom.id);
        return [...filtered, existingChat];
      }
      
      // If chat doesn't exist, add it
      // Mark as read when opening
      if (chatRoom.unread_count > 0) {
        apiService.chat.markMessagesRead(chatRoom.id).then(() => {
          fetchChatRooms();
        });
      }
      return [...prev, { id: chatRoom.id, chatRoom, minimized: false }];
    });
    
    setIsOpen(true);
    setShowConversationList(false);
  }, [fetchChatRooms]);

  // Close a chat window
  const handleCloseChat = (chatRoomId: number) => {
    setOpenChats(prev => prev.filter(chat => chat.id !== chatRoomId));
    if (openChats.length === 1) {
      setIsOpen(false);
    }
  };

  // Minimize a chat window
  const handleMinimizeChat = (chatRoomId: number) => {
    setOpenChats(prev => prev.map(chat => 
      chat.id === chatRoomId ? { ...chat, minimized: true } : chat
    ));
  };

  // Restore a minimized chat
  const handleRestoreChat = (chatRoomId: number) => {
    setOpenChats(prev => prev.map(chat => 
      chat.id === chatRoomId ? { ...chat, minimized: false } : chat
    ));
    setIsOpen(true);
  };

  // Toggle conversation list - fetch on open
  const toggleConversationList = () => {
    const willShow = !showConversationList;
    setShowConversationList(willShow);
    if (willShow) {
      // Fetch when opening the conversation list
      fetchChatRooms();
    }
  };

  // Handle new message event
  useEffect(() => {
    const handleNewMessage = () => {
      fetchChatRooms();
    };

    const handleOpenChatRoom = (event: Event) => {
      const customEvent = event as CustomEvent;
      const chatRoom = customEvent.detail.chatRoom;
      if (chatRoom) {
        handleOpenChat(chatRoom);
      }
    };

    window.addEventListener('chatMessageSent', handleNewMessage);
    window.addEventListener('openChatRoom', handleOpenChatRoom);
    
    return () => {
      window.removeEventListener('chatMessageSent', handleNewMessage);
      window.removeEventListener('openChatRoom', handleOpenChatRoom);
    };
  }, [fetchChatRooms, handleOpenChat]);

  // Don't render if user is not authenticated, on chat page, or on scanner pages
  if (!isAuthenticated || shouldHide) {
    return null;
  }

  return (
    <div className="global-chat-widget">
      {/* Conversation List Sidebar */}
      {showConversationList && (
        <div className="conversation-list-sidebar">
          <div className="conversation-list-header">
            <h5 className="mb-0">Messages</h5>
            <Button
              variant="link"
              className="p-0"
              onClick={toggleConversationList}
            >
              <i className="fas fa-times"></i>
            </Button>
          </div>
          
          <div className="conversation-list-body">
            {loading && (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" variant="primary" />
              </div>
            )}

            {error && (
              <Alert variant="danger" className="m-2">
                <small>{error}</small>
              </Alert>
            )}

            {!loading && chatRooms.length === 0 && (
              <div className="text-center py-4 text-muted">
                <i className="fas fa-comments fa-2x mb-3 d-block"></i>
                <p className="mb-0">No conversations yet</p>
                <small>Start a conversation from an event page</small>
              </div>
            )}

            {chatRooms.map((chatRoom) => (
              <div
                key={chatRoom.id}
                className={`conversation-item ${chatRoom.has_unread ? 'unread' : ''}`}
                onClick={() => handleOpenChat(chatRoom)}
              >
                <div className="conversation-avatar">
                  <i className="fas fa-comments"></i>
                </div>
                <div className="conversation-content">
                  <div className="conversation-name">{chatRoom.name}</div>
                  <div className="conversation-preview">
                    {chatRoom.last_message ? (
                      <>
                        <span className="sender-name">{chatRoom.last_message.sender_name}: </span>
                        <span>{chatRoom.last_message.content}</span>
                      </>
                    ) : (
                      <span className="text-muted">No messages yet</span>
                    )}
                  </div>
                </div>
                <div className="conversation-meta">
                  <div className="conversation-time">{chatRoom.last_message_time_ago}</div>
                  {chatRoom.unread_count > 0 && (
                    <span className="unread-badge">{chatRoom.unread_count}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Open Chat Windows */}
      <div className="chat-windows-container">
        {openChats.map((openChat, index) => (
          <div
            key={openChat.id}
            className={`chat-window ${openChat.minimized ? 'minimized' : ''}`}
            style={{ 
              right: openChat.minimized ? `${20 + index * 60}px` : `${20 + index * 20}px`,
              bottom: openChat.minimized ? '20px' : `${80 + index * 20}px`,
              zIndex: openChat.minimized ? 1053 + index : 1052 + index
            }}
          >
            {openChat.minimized ? (
              <div className="chat-window-minimized">
                <div className="minimized-header" onClick={() => handleRestoreChat(openChat.id)}>
                  <span className="minimized-name">{openChat.chatRoom.name}</span>
                  {openChat.chatRoom.unread_count > 0 && (
                    <span className="minimized-badge">{openChat.chatRoom.unread_count}</span>
                  )}
                </div>
                <Button
                  variant="link"
                  className="minimized-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseChat(openChat.id);
                  }}
                >
                  <i className="fas fa-times"></i>
                </Button>
              </div>
            ) : (
              <ChatRoom
                chatRoom={openChat.chatRoom}
                onClose={() => handleCloseChat(openChat.id)}
                onMinimize={() => handleMinimizeChat(openChat.id)}
                onMessageSent={() => fetchChatRooms()}
              />
            )}
          </div>
        ))}
      </div>

      {/* Main Chat Button */}
      <div className="chat-widget-button-container">
        <Button
          className="chat-widget-button bg-primary"
          onClick={() => {
            // Fetch when opening widget for the first time
            if (!showConversationList && chatRooms.length === 0) {
              fetchChatRooms();
            }
            toggleConversationList();
          }}
          title="Messages"
        >
          <i className="fas fa-comments"></i>
          {totalUnread > 0 && (
            <span className="chat-widget-badge">{totalUnread > 99 ? '99+' : totalUnread}</span>
          )}
        </Button>
      </div>
    </div>
  );
};

export default GlobalChatWidget;

